import { Box, Flex, Stack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  DiscountCode,
  FormControl,
  Input,
} from '@time-line/components/common';
import { useCart } from 'hooks/cart/useCart';
import { useDiscounts } from 'hooks/cart/useDiscounts';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const INPUT_NAME = 'code';

const validationSchema = z.object({
  [INPUT_NAME]: z.string().min(1, 'Discount code is required'),
});

type ValidationSchema = z.infer<typeof validationSchema>;

type CartDiscountProps = {
  isInputVisible?: boolean;
};

export const CartDiscount = ({ isInputVisible = true }: CartDiscountProps) => {
  const queryClient = useQueryClient();
  const cartQuery = useCart();
  const cart = cartQuery.data;
  const { addDiscounts, removeDiscounts, isPending } =
    useDiscounts(queryClient);

  // Form
  const { register, handleSubmit, watch } = useForm<ValidationSchema>({
    resolver: zodResolver(validationSchema),
  });

  const inputValue = watch(INPUT_NAME);

  const enteredDiscount = cart?.discountCodes?.find(
    (dC) => inputValue?.toUpperCase() === dC.code,
  );

  const onSubmit = handleSubmit(({ code }: { code: string }) => {
    addDiscounts([code]);
  });

  const removeDiscountCode = ({ code }: { code: string }) => {
    removeDiscounts([code]);
  };

  return (
    <Box data-testid="cart_discount">
      {isInputVisible && (
        <Flex as="form" onSubmit={onSubmit}>
          <FormControl
            errorMessage="Discount code could not be applied"
            isInvalid={enteredDiscount && !enteredDiscount.applicable}
          >
            <Input
              placeholder="Add a discount code"
              textTransform="uppercase"
              _placeholder={{
                textTransform: 'none',
              }}
              {...register(INPUT_NAME)}
            />
          </FormControl>
          <Button
            ml="12px"
            h="51px"
            variant="primary"
            lineHeight={1}
            isDisabled={!inputValue}
            isLoading={isPending}
            onClick={onSubmit}
            type="submit"
          >
            Apply
          </Button>
        </Flex>
      )}
      <Stack
        direction="row"
        mt="2xs"
        opacity={isPending ? 0.5 : 1}
        pointerEvents={isPending ? 'none' : 'auto'}
      >
        {cart?.discountCodes?.map(
          (discountCode) =>
            discountCode.applicable && (
              <DiscountCode
                key={discountCode.code}
                onRemove={() => removeDiscountCode({ code: discountCode.code })}
              >
                {discountCode.code}
              </DiscountCode>
            ),
        )}
      </Stack>
    </Box>
  );
};
